export type ContractStatus = "ATIVO" | "INATIVO" | "EM_CONFIGURACAO";

export interface IContractStatusEntry {
  workspaceId: string;
  status: ContractStatus;
  author: string;
  updatedAt: string;
  /**
   * CNPJ do contrato marcado. A marcação é feita por WORKSPACE (é o grão da
   * tela de Cadastro), mas a tela inicial do painel exibe por CNPJ e o
   * `GET /v2/operational-panel/home` **não** manda a lista de workspaces de
   * cada CNPJ — só `federalTaxId`, `buyerName` e `sellerName`.
   *
   * OPCIONAL, e continua opcional de propósito: a tela de Cadastro
   * (`SetupContract/{Health,Life}/WorkspaceShared/Rules/Rules.TabRegister.tsx`) é fluxo
   * essencial da plataforma e não foi alterada para passar este campo. Quem
   * preenche é o próprio `useContractStatus`, a partir do CNPJ que a tela já
   * informa na LEITURA (`getContractStatus(workspaceId, sellerName,
   * buyerFederalTaxId)`).
   *
   * Entrada sem este campo (gravada antes dele existir, ou gravada de um
   * contrato cujo cadastro não tem CNPJ preenchido) NÃO é ignorada: ela é
   * resolvida pelo `workspaceId`, que é o identificador que ela sempre tem,
   * contra o índice `WorkspaceCnpjIndex` — ver `resolveContractStatusForCnpj`.
   */
  buyerFederalTaxId?: string;
}

/**
 * `workspaceId` -> CNPJ (só dígitos). É o que permite resolver marcação antiga,
 * gravada só com `workspaceId`, no grão CNPJ da tela inicial. Aprendido em
 * runtime nos dois lugares em que o app já vê o par junto (`useContractStatus`:
 * a leitura da tela de Cadastro e o payload de `getCnpj`), nunca inventado.
 */
export type WorkspaceCnpjIndex = Readonly<Record<string, string>>;

/**
 * Regra de partida (Ponto 3 do plano de melhorias de lógica, 2026-08-10):
 * classificação por corretora, levantada no Postgres de produção (só
 * leitura) em 2026-08-10. Sem coluna nenhuma no banco — é intencional
 * (usuário pediu explicitamente "só frontend, não deve afetar nada no banco
 * de dados"). Nenhum critério de INATIVO foi definido ainda; nada cai nesse
 * estado por esta regra.
 *
 * Reconferido no Postgres de produção em 2026-08-11. O que esta regra
 * classifica como ATIVO hoje: Galcorr (6 CNPJs / 10 workspaces), Sopeh
 * (4 / 4) e a Enesa sob a Alper (10 / 17) — 20 CNPJs; com a Atlântica
 * (2026-08-24), **21**.
 *
 * A comparação é com `COALESCE(trading_name, corporate_name)` da
 * organização vendedora, que é o que o backend envia como `sellerName`.
 * Os valores exatos em produção são "Galcorr" e "Sopeh" — os
 * `corporate_name` ("Galcorr Corretora de Seguros LTDA", "Sopeh
 * Consultoria e Corretagem de Seguros LTDA") NÃO chegam aqui e não devem
 * ser usados. A versão anterior desta lista dizia "Corretora Galcorr",
 * que não existe no banco: nunca casou, e os 6 CNPJs da Galcorr caíam em
 * EM_CONFIGURACAO contra a intenção da regra.
 */
const ALWAYS_ATIVO_SELLER_NAMES = new Set(["Galcorr", "Sopeh"]);

const ALPER_SELLER_NAME = "ALPER CONSULTORIA E CORRETORA DE SEGUROS SA";

/**
 * CNPJs sob a Alper que nascem ATIVO pela regra de partida: a Enesa inteira
 * (10 CNPJs, conferidos em produção em 2026-08-11) **mais a Atlântica Hotels**
 * (`02223966000113`, adicionada em 2026-08-24).
 *
 * `48785828007727` faltava na versão anterior (2 workspaces criados em
 * 19/01/2026 — existiam bem antes do levantamento, foi omissão) e foi
 * confirmado como ATIVO pelo Gustavo em 2026-08-11.
 *
 * ## Atlântica (2026-08-24)
 *
 * O Gustavo já a tinha marcado como ATIVO pela tela de Cadastro, mas aquela
 * marcação grava em `localStorage` (`useContractStatus.ts`), então valia só no
 * navegador dele — ninguém mais do time via, e limpar dados do site desfazia.
 * Entrar aqui é o que torna a marcação durável e revisável. Em produção ela
 * entrega diariamente (último sucesso 23/08/2026, 1 workspace com a Alper,
 * produto Saúde).
 *
 * ## BRK Ambiental (2026-08-28)
 *
 * Os **27 CNPJs** do grupo, todos sob a Alper, 82 workspaces. Mesma história da
 * Atlântica: o Gustavo já tinha marcado alguns pela tela de Cadastro, mas aquilo
 * grava em `localStorage` e valia só no navegador dele — e marcar 27 na mão, um
 * a um, em cada máquina do time, não é caminho. Entrar aqui resolve os 27 de uma
 * vez, para todo mundo.
 *
 * Levantado no Postgres de produção (só leitura, 2026-08-28): nenhum está
 * `is_inactive` ou `is_blocked`; 108 execuções na janela de 90 dias, **todas
 * SUCCESS**, distribuídas em 19 dos 27; a última em 28/08/2026 08:10.
 *
 * Os **8 CNPJs sem execução nenhuma** entram sabendo o que vai aparecer: eles
 * caem em PARADO "nunca entregou", que é justamente o estado que a tela mostra
 * sem alarmar. É informação, não alerta — e é o desenho funcionando, não uma
 * lacuna. Como não há uma única falha no grupo, incluí-los não acende vermelho.
 *
 * ## O que ficou deliberadamente fora
 *
 * O mesmo levantamento (Postgres de produção, só leitura, 2026-08-24) achou
 * outros 16 CNPJs da Alper em EM_CONFIGURACAO, e a decisão do Gustavo foi
 * **não** incluí-los:
 *
 *   - **ENGEMAN**, 14 CNPJs (`08769549*`), com 5 a 7 workspaces cada e entrega
 *     de 0 a 2 dias. É o caso que mais chama atenção nesta lista: opera todo
 *     dia e segue fora dos ATIVO. Exclusão consciente, não esquecimento.
 *   - **Alper Demo** (`00000000000195`) e **Conducco Demo**
 *     (`00000000000196`) — CNPJ sintético, contratos de demonstração.
 *
 * `contractStatus.spec.ts` trava as duas exclusões, para que reverter seja uma
 * decisão explícita em vez de alguém "consertando" uma ausência que parece bug.
 */
const ALPER_ATIVO_FEDERAL_TAX_IDS = new Set([
  "02223966000113",
  "48785828000129",
  "48785828001010",
  "48785828007050",
  "48785828007727",
  "48785828008456",
  "48785828008960",
  "48785828009266",
  "48785828009347",
  "48785828009428",
  "48785828009509",
  // BRK Ambiental — 27 CNPJs, conferidos em produção em 2026-08-28.
  "00585900000148", // Limeira
  "02628150000170", // Cachoeiro de Itapemirim
  "05380441000180", // Mauá
  "08529701000124", // Jaguaribe
  "08630227000122", // Rio Claro
  "08774784000117", // Rio das Ostras
  "11609081000148", // Blumenau
  "12324465000187", // Santa Gertrudes
  "13015402000101", // Uruguaiana
  "14001255000183", // Porto Ferreira
  "17002138000122", // Macaé
  "17119291000134", // RMR/Goiana
  "18123402000149", // Goiás
  "18123402000220", // Goiás
  "18123402000300", // Goiás
  "18123402000491", // Goiás
  "19246473000100", // Manso
  "21480265000104", // Maranhão
  "21480265000295", // Maranhão
  "21480839000144", // Sumaré
  "24396489000120", // Participações
  "24396489000392", // Amb Part
  "24396489000473", // USI Rio Claro
  "24396489000716", // HUB Palmas
  "24396489001100", // HUB Blumenau
  "31444659000160", // Caçador
  "39580673000101", // RMM
]);

/** Regra de partida — resolve o default antes de overrides versionados/de sessão entrarem. */
export function getDefaultContractStatus(sellerName: string, buyerFederalTaxId: string): ContractStatus {
  if (ALWAYS_ATIVO_SELLER_NAMES.has(sellerName)) return "ATIVO";
  if (sellerName === ALPER_SELLER_NAME && ALPER_ATIVO_FEDERAL_TAX_IDS.has(buyerFederalTaxId)) return "ATIVO";
  return "EM_CONFIGURACAO";
}

/**
 * Overrides revisados por workspace, mesmo espírito do `muted.ts` (F0.4
 * caminho B): editar via PR, dá revisão e histórico à mudança. Só precisa de
 * entrada aqui quando o estado divergir da regra de partida acima (ex.: um
 * contrato específico virou INATIVO, ou algo fora da regra de seller virou
 * ATIVO manualmente pela tela de Cadastro).
 *
 * Exemplo:
 * { workspaceId: "ws-...", status: "INATIVO", author: "gustavo", updatedAt: "2026-08-10T00:00:00-03:00" },
 */
export const CONTRACT_STATUS_OVERRIDES: IContractStatusEntry[] = [];

/**
 * Ordem de VISIBILIDADE, não de severidade: "o que mais justifica mostrar o
 * CNPJ normalmente". Veio de `aggregate.ts` (Ponto 3, 2026-08-10), onde a
 * rolagem era feita sobre a lista de workspaces do CNPJ; mora aqui agora que
 * a tela inicial também precisa dela e não tem essa lista.
 */
const CONTRACT_STATUS_VISIBILITY_ORDER: ContractStatus[] = ["ATIVO", "EM_CONFIGURACAO", "INATIVO"];

/** O mais visível ganha. Lista vazia = nada conhecido, o chamador decide o default. */
export function mostVisibleContractStatus(statuses: ContractStatus[]): ContractStatus {
  return statuses.reduce(
    (best, current) =>
      CONTRACT_STATUS_VISIBILITY_ORDER.indexOf(current) < CONTRACT_STATUS_VISIBILITY_ORDER.indexOf(best) ? current : best,
    "INATIVO" as ContractStatus
  );
}

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * A qual CNPJ esta marcação se refere. `""` = nenhum que se possa afirmar.
 *
 * O CNPJ gravado na própria marcação vence o índice: ele foi registrado no
 * momento da marcação, o índice é conhecimento aprendido depois e pode estar
 * velho (workspace remanejado). Só quando a marcação não tem CNPJ é que o
 * `workspaceId` — o identificador que TODA marcação tem, inclusive a antiga —
 * é consultado no índice.
 */
export function resolveEntryFederalTaxId(entry: IContractStatusEntry, workspaceCnpjIndex: WorkspaceCnpjIndex): string {
  const own = onlyDigits(entry.buyerFederalTaxId ?? "");
  if (own) return own;
  return onlyDigits(workspaceCnpjIndex[entry.workspaceId] ?? "");
}

/**
 * Status de contrato no grão CNPJ — o grão da tela inicial. Duas decisões,
 * nas duas ordens em que elas importam:
 *
 * 1. **Marcação explícita vence a regra de partida.** A regra de partida é um
 *    palpite por corretora (lista fixa neste arquivo); quem abriu o Cadastro e
 *    escolheu "Inativo" tomou uma decisão sobre aquele contrato. Se existe
 *    qualquer marcação para o CNPJ, a regra de partida sai da conta — senão
 *    um CNPJ da lista fixa nunca poderia ser encerrado, e o estado INATIVO
 *    seria código morto.
 *
 * 2. **Entre marcações do MESMO CNPJ, o mais visível ganha** (ATIVO >
 *    EM_CONFIGURACAO > INATIVO). Um CNPJ pode ter workspaces com status
 *    divergente (operadoras/produtos diferentes, um encerrado e outro não);
 *    esconder exige unanimidade do que se sabe. Um workspace ainda ATIVO
 *    mantém o CNPJ inteiro na tela, vermelho se for o caso.
 *
 * 3. **Marcação sem CNPJ vale pelo `workspaceId`.** Entrada gravada antes do
 *    campo `buyerFederalTaxId` existir tem um identificador só — o
 *    `workspaceId` —, e ignorá-la não é neutro: quem já marcou um contrato como
 *    INATIVO o veria reaparecer na tela. Ela é resolvida pelo
 *    `workspaceCnpjIndex` e então vota igual às outras.
 *
 * Limite conhecido do item 2: o payload da tela inicial não lista os
 * workspaces do CNPJ, então "unanimidade" só pode ser apurada entre as
 * marcações existentes. Um CNPJ com dois workspaces, um marcado INATIVO e o
 * outro nunca tocado, resolve INATIVO — a tela antiga, que tinha a lista,
 * resolveria ATIVO. É a única regressão desta camada e ela mora aqui.
 *
 * CASO IRRESOLVÍVEL (item 3): marcação sem CNPJ cujo `workspaceId` ainda não
 * está no índice — nunca se abriu o Cadastro daquele workspace nem o CNPJ dele
 * no painel neste navegador. Não há como atribuí-la a CNPJ nenhum, então ela
 * não vota em nenhum: o erro cai no lado VISÍVEL (o CNPJ segue na tela pela
 * regra de partida, tipicamente ATIVO), porque esconder exige certeza e
 * atribuir a marcação por chute esconderia CNPJ errado.
 */
export function resolveContractStatusForCnpj(
  cnpj: { sellerName: string; federalTaxId: string },
  entries: IContractStatusEntry[],
  workspaceCnpjIndex: WorkspaceCnpjIndex = {}
): ContractStatus {
  const federalTaxId = onlyDigits(cnpj.federalTaxId);
  const explicitos = entries
    .filter((entry) => {
      const doEntry = resolveEntryFederalTaxId(entry, workspaceCnpjIndex);
      /** `""` de um lado nunca casa com o outro: sem CNPJ apurado não há atribuição. */
      return Boolean(doEntry) && Boolean(federalTaxId) && doEntry === federalTaxId;
    })
    .map((entry) => entry.status);

  if (explicitos.length > 0) return mostVisibleContractStatus(explicitos);
  return getDefaultContractStatus(cnpj.sellerName, federalTaxId);
}
